/**
 * CloudLedger API Client
 *
 * Type-safe RPC client using Hono.
 * Per Project Rules: NO native fetch() allowed - use this client only.
 */

import { hc } from "hono/client";
import type { AppType } from "@cloudledger/api";

// ============================================================================
// Configuration
// ============================================================================

const API_BASE_URL = import.meta.env.DEV
    ? "http://localhost:8787"
    : (import.meta.env.VITE_API_BASE_URL || "https://api.cloudledger.example.com");

// ============================================================================
// API Client
// ============================================================================

/**
 * Type-safe API client
 *
 * @example
 * ```ts
 * // ✅ Correct - uses typed RPC client
 * const res = await client.api.transactions.$get({
 *   query: { ledgerId: "...", page: 1, limit: 20 }
 * });
 * const data = await res.json();
 *
 * // ❌ Wrong - native fetch is forbidden
 * fetch("/api/transactions");
 * ```
 */
export const client = hc<AppType>(API_BASE_URL, {
    init: {
        credentials: "include",
    },
});

// ============================================================================
// Helper: Add custom headers (for BYOK AI Key)
// ============================================================================

/**
 * Create a client with custom AI API key header
 *
 * @param aiApiKey - User's OpenAI API key
 * @returns Client with X-Custom-AI-Key header
 */
export const createClientWithAIKey = (aiApiKey: string) => {
    return hc<AppType>(API_BASE_URL, {
        init: {
            credentials: "include",
        },
        headers: {
            "X-Custom-AI-Key": aiApiKey,
        },
    });
};

// ============================================================================
// Helper: Add auth token
// ============================================================================

/**
 * Create a client with auth token
 *
 * @param token - Session token
 * @returns Client with Authorization header
 */
export const createAuthenticatedClient = (token: string) => {
    return hc<AppType>(API_BASE_URL, {
        init: {
            credentials: "include",
        },
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
};

/**
 * Create a client with both auth and AI key/base URL/models
 *
 * @param token - Session token
 * @param aiApiKey - User's OpenAI API key
 * @param aiBaseUrl - Custom API base URL (for proxies or alternative providers)
 * @param chatModel - Custom chat model name
 * @param embeddingModel - Custom embedding model name
 * @returns Client with all headers
 */
export const createFullClient = (
    token: string,
    aiApiKey?: string,
    aiBaseUrl?: string,
    chatModel?: string,
    embeddingModel?: string
) => {
    const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
    };

    if (aiApiKey) {
        headers["X-Custom-AI-Key"] = aiApiKey;
    }

    if (aiBaseUrl) {
        headers["X-Custom-AI-Base-Url"] = aiBaseUrl;
    }

    if (chatModel) {
        headers["X-Custom-AI-Chat-Model"] = chatModel;
    }

    if (embeddingModel) {
        headers["X-Custom-AI-Embedding-Model"] = embeddingModel;
    }

    return hc<AppType>(API_BASE_URL, {
        init: {
            credentials: "include",
        },
        headers,
    });
};

// ============================================================================
// Type Helpers
// ============================================================================

/**
 * Infer response type from an RPC endpoint
 *
 * @example
 * type TransactionsResponse = InferResponse<typeof client.api.transactions.$get>;
 */
export type InferResponse<T extends (...args: never[]) => Promise<Response>> =
    Awaited<ReturnType<Awaited<ReturnType<T>>["json"]>>;

/**
 * Extract data type from API response
 */
export type ExtractData<T> = T extends { success: true; data: infer D }
    ? D
    : never;
